import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, map, switchMap, combineLatest, of } from 'rxjs';
import { TimeEntry, TimeOffRequest } from '../interfaces/time-tracking.interface';
import { AuthService } from './auth.service';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { firstValueFrom } from 'rxjs';
import { UserProfile } from '../interfaces/user-profile.interface';

interface UserData {
  uid?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TimeTrackingService {
  constructor(
    private firestore: AngularFirestore,
    private authService: AuthService
  ) {}

  // Time Entry Methods
  async clockIn(description: string, location: string): Promise<void> {
    const user = await this.authService.getCurrentUser();
    if (!user) throw new Error('No authenticated user');

    const timeEntry: TimeEntry = {
      userId: user.uid,
      startTime: new Date(),
      description,
      location: location as 'office' | 'remote' | 'field',
      status: 'active'
    };

    await this.firestore.collection('timeEntries').add(timeEntry);
  }

  async clockOut(entryId: string): Promise<void> {
    await this.firestore.collection('timeEntries').doc(entryId).update({
      endTime: new Date(),
      status: 'completed'
    });
  }

  getCurrentTimeEntry(userId: string): Observable<TimeEntry | null> {
    return this.firestore
      .collection<TimeEntry>('timeEntries', ref => 
        ref.where('userId', '==', userId)
           .where('status', '==', 'active')
           .limit(1)
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map(entries => {
          if (!entries.length) return null;
          return this.convertTimestamps(entries[0]);
        })
      );
  }

  getRecentEntries(userId: string): Observable<TimeEntry[]> {
    return this.firestore
      .collection<TimeEntry>('timeEntries', ref => 
        ref.where('userId', '==', userId)
           .orderBy('startTime', 'desc')
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map(entries => entries.map(entry => this.convertTimestamps(entry)))
      );
  }

  private convertTimestamps(entry: any): TimeEntry {
    const convertToDate = (timestamp: any): Date | null => {
      if (!timestamp) return null;
      if (timestamp instanceof Date) return timestamp;
      if (timestamp.toDate && typeof timestamp.toDate === 'function') return timestamp.toDate();
      return new Date(timestamp);
    };

    return {
      ...entry,
      startTime: convertToDate(entry.startTime),
      endTime: convertToDate(entry.endTime)
    };
  }

  // Time Off Methods
  async requestTimeOff(request: Partial<TimeOffRequest>): Promise<void> {
    const user = await firstValueFrom(this.authService.user$);
    if (!user) throw new Error('No user logged in');

    const requestDoc = {
      ...request,
      // Only set userId if not provided in the request
      userId: request.userId || user.uid,
      userName: user.displayName || 'Unknown',
      email: user.email
    };

    await this.firestore.collection('timeOffRequests').add(requestDoc);
  }

  getUserTimeOffRequests(userId: string): Observable<TimeOffRequest[]> {
    return this.firestore
      .collection<TimeOffRequest>('timeOffRequests', ref => 
        ref.where('userId', '==', userId)
           .orderBy('startDate', 'desc')
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map(requests => requests.map(request => ({
          ...request,
          startDate: this.convertToDate(request.startDate),
          endDate: this.convertToDate(request.endDate),
          requestedAt: this.convertToDate(request.requestedAt)
        }))),
        map(requests => requests.sort((a, b) => {
          if (a.startDate.getTime() === b.startDate.getTime()) {
            return b.endDate.getTime() - a.endDate.getTime();
          }
          return b.startDate.getTime() - a.startDate.getTime();
        }))
      );
  }

  getAllTimeOffRequests(): Observable<TimeOffRequest[]> {
    return this.firestore
      .collection<TimeOffRequest>('timeOffRequests', ref => 
        ref.orderBy('requestedAt', 'desc')
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map(requests => requests.map(request => ({
          ...request,
          startDate: this.convertToDate(request.startDate),
          endDate: this.convertToDate(request.endDate),
          requestedAt: this.convertToDate(request.requestedAt)
        })))
      );
  }

  getTimeOffRequest(requestId: string): Observable<TimeOffRequest | null> {
    return this.firestore
      .collection<TimeOffRequest>('timeOffRequests')
      .doc(requestId)
      .valueChanges({ idField: 'id' })
      .pipe(
        map(request => {
          if (!request) return null;
          return {
            ...request,
            startDate: this.convertToDate(request.startDate),
            endDate: this.convertToDate(request.endDate),
            requestedAt: this.convertToDate(request.requestedAt)
          };
        })
      );
  }

  private convertToDate(timestamp: any): Date {
    if (timestamp instanceof Date) return timestamp;
    if (timestamp && typeof timestamp.toDate === 'function') return timestamp.toDate();
    return new Date(timestamp);
  }

  async updateTimeOffRequest(requestId: string, request: Partial<TimeOffRequest>): Promise<void> {
    // No need to get current user since we're preserving original user info
    const requestDoc = {
      ...request,
      updatedAt: new Date()
    };

    await this.firestore.collection('timeOffRequests').doc(requestId).update(requestDoc);
  }

  async deleteTimeOffRequest(requestId: string): Promise<void> {
    try {
      // First get the current request data
      const requestDoc = await this.firestore
        .collection<TimeOffRequest>('timeOffRequests')
        .doc(requestId)
        .get()
        .toPromise();

      const requestData = requestDoc?.data() as TimeOffRequest | undefined;

      // Delete the request
      await this.firestore
        .collection('timeOffRequests')
        .doc(requestId)
        .delete();

      // Optionally log the cancellation
      if (requestData) {
        await this.firestore.collection('timeOffCancellations').add({
          originalRequest: requestData,
          requestId: requestId,
          cancelledAt: new Date(),
          previousStatus: requestData.status
        });
      }
    } catch (error) {
      console.error('Error deleting time off request:', error);
      throw error; // Let the component handle the error notification
    }
  }

  // Leave Balance Methods
  async addManualTimeEntry(entry: Partial<TimeEntry>): Promise<void> {
    const user = await this.authService.getCurrentUser();
    if (!user) throw new Error('No authenticated user');

    const timeEntry: TimeEntry = {
      userId: user.uid,
      startTime: entry.startTime || null,
      endTime: entry.endTime || null,
      description: entry.description || '',
      location: entry.location,
      status: 'completed'
    };

    await this.firestore.collection('timeEntries').add(timeEntry);
  }

  async deleteTimeEntry(entryId: string): Promise<void> {
    try {
      await this.firestore.collection('timeEntries').doc(entryId).delete();
    } catch (error) {
      console.error('Error deleting time entry:', error);
      throw error;
    }
  }

  async updateTimeEntry(entryId: string, updates: Partial<TimeEntry>): Promise<void> {
    try {
      await this.firestore.collection('timeEntries').doc(entryId).update(updates);
    } catch (error) {
      console.error('Error updating time entry:', error);
      throw error;
    }
  }

  getAllTimeEntries(): Observable<TimeEntry[]> {
    return this.firestore
      .collection<TimeEntry>('timeEntries', ref => 
        ref.orderBy('startTime', 'desc')
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map(entries => entries.map(entry => this.convertTimestamps(entry)))
      );
  }

  getApprovedTimeOffForToday() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return this.firestore
      .collection<TimeOffRequest>('timeOffRequests', ref => 
        ref.where('status', '==', 'approved')
           .where('startDate', '<=', today)
           .where('endDate', '>=', today)
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        switchMap(requests => {
          // Get unique user IDs from requests
          const userIds = [...new Set(requests.map(r => r.userId))];
          
          // If there are no requests, return empty array
          if (userIds.length === 0) return of([]);

          // Get user documents for all requesters
          return combineLatest([
            of(requests),
            combineLatest(
              userIds.map(uid => 
                this.firestore.doc<UserData>(`users/${uid}`).valueChanges()
                  .pipe(map(user => ({ uid, ...user } as UserData)))
              )
            )
          ]);
        }),
        map(([requests, users]) => {
          const userMap = new Map(users.map(u => [u.uid!, u]));
          
          return requests.map(request => ({
            ...request,
            userName: userMap.get(request.userId)?.displayName || 
                     `${userMap.get(request.userId)?.firstName || ''} ${userMap.get(request.userId)?.lastName || ''}`.trim() ||
                     userMap.get(request.userId)?.email ||
                     'Unknown User'
          }));
        })
      );
  }

  // Add this new method specifically for the vacation calendar
  getApprovedTimeOffRequests(): Observable<TimeOffRequest[]> {
    return this.firestore
      .collection<TimeOffRequest>('timeOffRequests', ref => 
        ref.where('status', '==', 'approved')
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map(requests => requests.map(request => ({
          ...request,
          startDate: this.convertToDate(request.startDate),
          endDate: this.convertToDate(request.endDate),
          requestedAt: this.convertToDate(request.requestedAt)
        })))
      );
  }

  getAllUsers(): Observable<UserProfile[]> {
    return this.firestore
      .collection<UserProfile>('users')
      .valueChanges({ idField: 'uid' });
  }
} 